import {
  faCheck,
  faCircleNodes,
  faLink,
  faStar as faStarRegular,
} from "@fortawesome/pro-regular-svg-icons";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { Button, Icon } from "@intility/bifrost-react";
import { Link, useNavigate } from "react-router";
import type { ClientListItem } from "../../api/types";
import { ClientPurposeBadge } from "../../components/ClientTypeBadge";
import { GoToTag } from "../../components/GoToTag";
import { RoleBadge } from "../../components/RoleBadge";
import { clientPath } from "../../lib/clientPath";
import { FROM_MYHUB } from "../../lib/fromMyHub";
import s from "./MyPlatforms-PlatformCard.module.css";

// A client as a card, built like a platform's: the star, the name with the
// Client ID under it, and the user's role at the top; the purpose and the
// platform under that.
export default function ClientCard({
  client,
  role,
  platformName,
  onToggleStar,
  starred,
}: {
  client: ClientListItem;
  /** The user's role, from the client's own record; none while it loads. */
  role: string | undefined;
  /** The name of the client's platform; none when it is standalone. */
  platformName: string | undefined;
  onToggleStar: () => void;
  starred: boolean;
}) {
  const navigate = useNavigate();
  // The wired-up claim a maintainer has made on the client (ADR-0060).
  const wired = client.wiredUpAt != null;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the client name inside is a real link, which is the keyboard route to the same page.
    // biome-ignore lint/a11y/noStaticElementInteractions: see above.
    <div
      className={s.card}
      onClick={(event) => {
        // Buttons and links in the card handle their own click.
        if (
          event.target instanceof Element &&
          event.target.closest("a, button")
        )
          return;
        navigate(clientPath(client), { state: FROM_MYHUB });
      }}
    >
      {/* As tall as its content, with 8px under the Client ID before the border,
          instead of the platform card's fixed height. */}
      <div className={s.top} style={{ height: "auto", paddingBottom: 8 }}>
        <span className={s.star}>
          <Button
            aria-label={`Favourite ${client.name}`}
            aria-pressed={starred}
            noPadding
            onClick={onToggleStar}
            state="neutral"
            variant="flat"
          >
            <Icon
              className={starred ? "bfc-warning" : "bfc-base-2"}
              icon={starred ? faStar : faStarRegular}
            />
          </Button>
        </span>
        <div className={s.text}>
          <Link
            className={`bf-neutral-link ${s.nameLink}`}
            state={FROM_MYHUB}
            to={clientPath(client)}
          >
            <span className={s.name}>{client.name}</span>
          </Link>
          {/* Bifrost small, on one line. */}
          <span
            className="bf-small bfc-base-2"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {client.clientId}
          </span>
        </div>
        {role && (
          <span className={s.role}>
            <RoleBadge role={role} />
          </span>
        )}
      </div>

      {/* The status banner, like the platform card's: neutral once the client is
          wired up, yellow while it still needs to be. It splits the card in
          two, under the Client ID. */}
      <div
        className={`${s.statusBar} ${wired ? "bfc-neutral-fade-bg" : "bfc-warning-fade-bg"}`}
        style={{ fontWeight: 600 }}
      >
        <Icon icon={wired ? faCheck : faLink} />
        {wired ? "Wired up" : "Needs to wire up"}
      </div>

      <div className={s.clients}>
        <ClientPurposeBadge
          appType={client.appType}
          purposeTag={client.purposeTag}
        />
        {client.platformId ? (
          <GoToTag
            kind="platform"
            onGo={() =>
              navigate(`/platforms/${client.platformId}`, {
                state: FROM_MYHUB,
              })
            }
            style={{ maxWidth: "100%" }}
            title={platformName}
          >
            <span className={s.tagLabel}>
              {/* The icon a platform has where it has no logo. */}
              <Icon icon={faCircleNodes} marginRight />
              {platformName ?? client.platformId}
            </span>
          </GoToTag>
        ) : (
          <em className="bfc-base-2">No platform</em>
        )}
      </div>
    </div>
  );
}
